import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination, paginate } from "@/components/list-pagination";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { InviteStaffDialog } from "@/features/staff/invite-staff/dialog";
import { staffListQuery } from "@/features/staff/queries";
import { StaffList } from "@/features/staff/staff-list";
import { m } from "@/lib/paraglide/messages.js";

const PAGE_SIZE = 10;

// ?page= is route state (guide, rule 16): a link to page 2 opens page 2. Anything else reads as 1.
const StaffSearch = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /staff/staff: every operator; a superadmin also invites and disables. The nav and these
// buttons are a courtesy, the API's staffGuard is the gate (guide, rule 14).
export const Route = createFileRoute("/staff/_authenticated/staff")({
  validateSearch: StaffSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(staffListQuery()),
  staticData: { crumb: m.staff_nav_staff },
  component: StaffPage,
});

function StaffPage() {
  const { staffMember } = Route.useRouteContext();
  const { page: asked = 1 } = Route.useSearch();
  const { data: members } = useSuspenseQuery(staffListQuery());
  // The API sends every operator (a handful), so the page is cut here.
  const { page, pages, items, range } = paginate(members, asked, PAGE_SIZE);
  const superadmin = staffMember.role === "superadmin";
  return (
    <>
      {/* The page's heading: shadcn's Item as shipped, its action the invitation. */}
      <Item>
        <ItemContent>
          <ItemTitle>{m.staff_nav_staff()}</ItemTitle>
          <ItemDescription>{m.staff_page_description()}</ItemDescription>
        </ItemContent>
        {superadmin && (
          <ItemActions>
            <InviteStaffDialog />
          </ItemActions>
        )}
      </Item>
      <StaffList
        members={items}
        currentStaffId={staffMember.id}
        canDisable={superadmin}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
    </>
  );
}
